import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Stack
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface User {
  id: number;
  name: string;
  email: string;
}

interface TransferRequest {
  id: number;
  userId: number;
  profession: string;
  currentLocation: string;
  targetLocations: string[];
  notes?: string;
  User: User;
}

interface TransferMatch {
  id: number;
  aRequestId: number;
  bRequestId: number;
  score: number;
  status: string;
  createdAt: string;
  TransferRequest_TransferMatch_aRequestIdToTransferRequest: TransferRequest;
  TransferRequest_TransferMatch_bRequestIdToTransferRequest: TransferRequest;
}

const NewMatching: React.FC = () => {
  const { token, user } = useAuth();
  const [matches, setMatches] = useState<TransferMatch[]>([]);
  const [message, setMessage] = useState<string>('');
  const [runningMatch, setRunningMatch] = useState(false);

  const loadMatches = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  }, [token]);

  const runMatching = async () => {
    setRunningMatch(true);
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/match/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessage(`Matching completed! Found ${data.matchesFound} potential matches.`);
        await loadMatches();
      } else {
        setMessage('Failed to run matching algorithm');
      }
    } catch (error) {
      setMessage('Failed to run matching algorithm');
    } finally {
      setRunningMatch(false);
    }
  };

  const updateMatchStatus = async (matchId: number, status: string) => {
    try {
      const res = await fetch(`${API_BASE}/matches/${matchId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        await loadMatches();
        setMessage(`Match ${status} successfully!`);
      } else {
        setMessage(`Failed to ${status} match`);
      }
    } catch (error) {
      setMessage(`Failed to ${status} match`);
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 120) return 'success';
    if (score >= 110) return 'warning';
    return 'default';
  };

  const getMatchScoreLabel = (score: number) => {
    if (score >= 120) return 'Excellent Match';
    if (score >= 110) return 'Good Match';
    return 'Basic Match';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Transfer Matches
        </Typography>
        <Button
          variant="contained"
          startIcon={runningMatch ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={runMatching}
          disabled={runningMatch}
        >
          {runningMatch ? 'Running...' : 'Find Matches'}
        </Button>
      </Box>

      {message && (
        <Alert sx={{ mb: 3 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {matches.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SwapIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No matches found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Click "Find Matches" to search for potential transfer opportunities
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={runMatching}
              disabled={runningMatch}
            >
              Find Matches
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {matches.map((match) => {
            const isUserRequestA = match.TransferRequest_TransferMatch_aRequestIdToTransferRequest.userId === user?.id;
            const userRequest = isUserRequestA 
              ? match.TransferRequest_TransferMatch_aRequestIdToTransferRequest
              : match.TransferRequest_TransferMatch_bRequestIdToTransferRequest;
            const otherRequest = isUserRequestA 
              ? match.TransferRequest_TransferMatch_bRequestIdToTransferRequest
              : match.TransferRequest_TransferMatch_aRequestIdToTransferRequest;

            return (
              <Grid item xs={12} key={match.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={getMatchScoreLabel(match.score)}
                          color={getMatchScoreColor(match.score)}
                          icon={<StarIcon />}
                        />
                        <Chip
                          label={`Score: ${match.score}`}
                          variant="outlined"
                        />
                        <Chip
                          label={match.status}
                          color={getStatusColor(match.status)}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(match.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      {/* User's Request */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, backgroundColor: 'primary.50' }}>
                          <Typography variant="h6" gutterBottom color="primary">
                            Your Request
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WorkIcon fontSize="small" />
                              <Typography variant="body2">{userRequest.profession}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon fontSize="small" />
                              <Typography variant="body2">
                                From: {userRequest.currentLocation}
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              To: {userRequest.targetLocations.join(', ')}
                            </Typography>
                            {userRequest.notes && (
                              <Typography variant="body2" color="textSecondary">
                                Notes: {userRequest.notes}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Other User's Request */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, backgroundColor: 'secondary.50' }}>
                          <Typography variant="h6" gutterBottom color="secondary">
                            Potential Match
                          </Typography>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon fontSize="small" />
                              <Typography variant="body2" fontWeight="bold">
                                {otherRequest.User.name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <WorkIcon fontSize="small" />
                              <Typography variant="body2">{otherRequest.profession}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon fontSize="small" />
                              <Typography variant="body2">
                                From: {otherRequest.currentLocation}
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              To: {otherRequest.targetLocations.join(', ')}
                            </Typography>
                            {otherRequest.notes && (
                              <Typography variant="body2" color="textSecondary">
                                Notes: {otherRequest.notes}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>
                    </Grid>

                    {match.status === 'suggested' && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<AcceptIcon />}
                          onClick={() => updateMatchStatus(match.id, 'accepted')}
                        >
                          Accept Match
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => updateMatchStatus(match.id, 'rejected')}
                        >
                          Reject Match
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default NewMatching;
